import { useGridApiRef, Table } from '@repo/go-table';
import type { GridColumnResizeParams, GridEventListener } from '@repo/go-table';
import { ConfigContext } from '@repo/go-ui';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'throttle-debounce';

import type { INotificationsService } from '../../shared/INotificationsService';
import tableStyles from '../AlertsManagerTable.module.css';
import { isColumnsOrderModelEqual } from '../utils';

import { RecommendationsContextPanel } from './components/RecommendationsContextPanel';
import { RecommendationsTableSettings } from './components/RecommendationsTableSettings';
import { RecommendationsToolbar } from './components/RecommendationsToolbar';
import { openExitEditModeConfirmModal } from './openExitEditModeConfirmModal';
import { RecommendationsTableStore } from './RecommendationsTableStore';
import type { Recommendation } from './services/Recommendation.entity';
import type { RecommendationsService } from './services/RecommendationsService';
import { useColumnGroups } from './useColumnGroups';
import { useRecommendationsColumns } from './useColumns';
import { useRecommendationContextOptions } from './useContextOptions';

export type RecommendationsTableProps = {
  service: RecommendationsService;
  notifications: INotificationsService;
  leftToolbarContent?: ReactNode;
  isAltNames: boolean;
};

const isNativeClipboardTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLTextAreaElement ||
  (target instanceof HTMLInputElement && ['text', 'number', 'date'].includes(target.type));

export const RecommendationsTable = observer(function RecommendationsTable({
  service,
  notifications,
  leftToolbarContent,
  isAltNames,
}: RecommendationsTableProps) {
  const configProvider = useContext(ConfigContext);
  const { i18n } = useTranslation();
  const tableApi = useGridApiRef();
  const [mainContentElement, setMainContentElement] = useState<HTMLDivElement | null>(null);
  const themeClassName =
    configProvider.theme === 'light' || configProvider.theme === 'lightBordered'
      ? 'go-widget-theme-light'
      : 'go-widget-theme-dark';

  const store = useMemo(
    () => new RecommendationsTableStore(tableApi, notifications, service),
    [service, tableApi, notifications],
  );

  const columns = useRecommendationsColumns(service, store, isAltNames);
  const contextDirectories = useRecommendationContextOptions(service);
  const { displayManager } = store;

  const columnFields = useMemo(() => columns.map((c) => c.field), [columns]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleWidthChange = useCallback(
    debounce(300, ({ colDef: { field }, width }: GridColumnResizeParams) => {
      if (!displayManager) return;
      const cols = tableApi.current.getAllColumns();
      const updated: Record<string, number> = {};
      const columnsWidth = displayManager.getColumnsWidth();
      cols.forEach((column) => {
        if (column.field === field) {
          updated[field] = width;
        } else if (columnsWidth && !columnsWidth[column.field]) {
          updated[column.field] = column.computedWidth;
        }
      });
      displayManager.onWidthChange(updated);
    }),
    [displayManager, tableApi],
  );

  useEffect(() => {
    return store.init();
  }, [store]);

  const columnsOrderModel = displayManager?.columnsOrderModel;

  const handleColumnsOrderModelChange = useCallback(
    (orderModel: string[]) => {
      const isOrderModelValid =
        orderModel.length === columnFields.length && orderModel.every((field) => columnFields.includes(field));

      if (!isOrderModelValid) {
        return;
      }
      const hasSavedOrderModel = Boolean(columnsOrderModel?.length);
      const hasCustomSavedOrderModel = hasSavedOrderModel && !isColumnsOrderModelEqual(columnsOrderModel, columnFields);
      const isIncomingDefaultOrderModel = isColumnsOrderModelEqual(orderModel, columnFields);

      if (hasCustomSavedOrderModel && isIncomingDefaultOrderModel) {
        return;
      }

      displayManager?.onColumnsOrderModelChange(orderModel);
    },
    [columnFields, columnsOrderModel, displayManager],
  );

  const groups = useColumnGroups();

  useEffect(() => {
    const columnsWidth = displayManager?.getColumnsWidth();
    if (!displayManager || !columnsWidth) return;
    for (const column of tableApi.current.getAllColumns()) {
      if (columnsWidth[column.field] === column.width) continue;
      tableApi.current.setColumnWidth(column.field, columnsWidth[column.field]);
    }
  }, [tableApi, displayManager]);

  const onClipboardCopy: GridEventListener<'clipboardCopy'> = useCallback(
    (_, event) => {
      event.defaultMuiPrevented = true;
      let timer: number | undefined;
      if (timer != null) return;
      store.handleCopy();
      timer = window.setTimeout(() => {
        timer = undefined;
      }, 300);
    },
    [store],
  );

  const onCellKeydown: GridEventListener<'cellKeyDown'> = useCallback(
    (params, event) => {
      if (event.code === 'KeyV' && (event.metaKey || event.ctrlKey)) {
        if (event.repeat || isNativeClipboardTarget(event.target)) {
          return;
        }

        event.defaultMuiPrevented = true;
        event.preventDefault();
        event.stopPropagation();
        store.handlePaste({ id: String(params.id), field: params.field });
      }

      if (event.code === 'Delete') {
        event.defaultMuiPrevented = true;
        store.handleDelete(params);
      }
    },
    [store],
  );

  useEffect(() => {
    const handlePaste = (event: KeyboardEvent) => {
      if (event.code !== 'KeyV' || (!event.ctrlKey && !event.metaKey)) return;
      if (event.repeat || isNativeClipboardTarget(event.target)) return;

      event.preventDefault();
      store.handlePaste();
    };

    document.addEventListener('keydown', handlePaste);
    return () => {
      document.removeEventListener('keydown', handlePaste);
    };
  }, [store]);

  const getSelectedRowsIds = useCallback(() => {
    const selectedRows = tableApi.current.getSelectedRows();
    return Array.from(selectedRows.keys()).map((key) => key.toString());
  }, [tableApi]);

  const selectedRowIds = useMemo(() => {
    const model = store.selectedRows;
    if (!Array.isArray(model)) return [];
    return model.map((id) => String(id));
  }, [store.selectedRows]);

  const save = () => {
    service
      .saveRecommendations()
      .then(() => service.setEditing(false))
      .catch((error) => console.error(error));
  };

  const cancel = () => {
    const exitEditModeAsync = async () => {
      if (service.hasUnsavedChanges) {
        const isConfirmed = await openExitEditModeConfirmModal(configProvider, i18n);
        if (!isConfirmed) {
          return;
        }
        await flowResult(service.discardChanges());
      }
      service.setEditing(false);
    };

    exitEditModeAsync().catch((error) => {
      console.error(error);
    });
  };

  return (
    <>
      <div
        className={themeClassName}
        style={{ height: '100%', position: 'relative' }}
        ref={(r) => setMainContentElement(r)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '0 0 16px' }}>
          <RecommendationsToolbar
            isEditModeActive={store.isEditing}
            onEnterEditMode={() => service.setEditing(true)}
            onAddRow={() => service.addNewRecommendationItems()}
            onDelete={() => service.deleteRecommendationItems(getSelectedRowsIds())}
            onChooseRow={() => store.setIsRowSelectionModeOn(!store.isRowSelectionModeOn)}
            onSettingOpen={() => store.setIsTableSettingsOpen(true)}
            save={save}
            cancel={cancel}
            leftBlockElement={leftToolbarContent}
            deleteDisabled={service.loading || store.selectedRows.length === 0}
          />
          <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <Table<Recommendation>
              initialState={{
                sorting: { sortModel: displayManager.sortingModel },
                columns: { columnVisibilityModel: displayManager.columnsVisibilityModel },
                pinnedColumns: displayManager.pinnedColumns,
              }}
              columnGroupingModel={groups}
              sortModel={displayManager.sortingModel}
              checkboxSelection={store.isRowSelectingEnabled}
              columnVisibilityModel={displayManager.columnsVisibilityModel}
              columnsOrderModel={displayManager.columnsOrderModel}
              onColumnVisibilityModelChange={displayManager.onColumnVisibilityChange}
              onColumnsOrderModelChange={handleColumnsOrderModelChange}
              onPinnedColumnsChange={displayManager.onPinChange}
              onSortModelChange={displayManager.onSortChange}
              onColumnResize={handleWidthChange}
              onRowSelectionModelChange={store.setSelectedRows}
              onClipboardCopy={onClipboardCopy}
              onCellKeyDown={onCellKeydown}
              isCellEditable={(params) => store.isCellEditable(params)}
              getRowId={(row) => row.entityId}
              apiRef={tableApi}
              className={tableStyles.table}
              columns={columns}
              rows={service.rows}
              loading={service.loading}
              editMode="cell"
              cellSelection
            />
          </div>
        </div>
        {store.isContextPanelOpen && mainContentElement && (
          <RecommendationsContextPanel
            selectedRowIds={selectedRowIds}
            store={store}
            directories={contextDirectories}
            onClose={() => store.setIsContextPanelOpen(false)}
            container={mainContentElement}
          />
        )}
      </div>
      <RecommendationsTableSettings
        displayManager={displayManager}
        columns={columns}
        groups={groups}
        open={store.isTableSettingsOpen}
        onClose={() => store.setIsTableSettingsOpen(false)}
      />
    </>
  );
});
